import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, TrendingUp, Smile, TrendingDown, Eye, AlertTriangle,
  BarChart3, Users, DollarSign, Star, ChevronDown, ChevronLeft,
  ChevronRight, ShieldCheck, Layers, Package, HelpCircle
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const WHATSAPP_URL = 'https://wa.me/5588996527079'
const DASHBOARD = 'https://lookerstudio.google.com/s/nQHCdnAf6dw';

const painPoints = [
  {
    icon: Eye,
    title: 'Falta de Clareza nos Dados',
    description: 'Sem visibilidade real sobre a saúde de cada cliente, decisões são tomadas no escuro e oportunidades de retenção passam despercebidas.'
  },
  {
    icon: TrendingDown,
    title: 'Métricas Escondidas',
    description: 'MRR, LTV e Churn Rate ficam espalhados em planilhas desorganizadas. Você não sabe o real valor de cada cliente até perdê-lo.'
  },
  {
    icon: AlertTriangle,
    title: 'Churn Inesperado',
    description: 'Clientes cancelam sem aviso porque ninguém monitorava os sinais. A falta de acompanhamento transforma retenção em loteria.'
  }
];

const solutionMetrics = [
  { icon: DollarSign, label: 'MRR', description: 'Receita Recorrente Mensal em tempo real' },
  { icon: TrendingDown, label: 'Churn Rate', description: 'Taxa de cancelamento sob controle' },
  { icon: Users, label: 'LTV', description: 'Lifetime Value de cada cliente' },
  { icon: Star, label: 'Satisfação', description: 'Indicadores de satisfação do cliente' },
  { icon: BarChart3, label: 'Ativos vs Encerrados', description: 'Comparativo visual e detalhado' }
];

const solutionItems = [
  {
    icon: Layers,
    title: '3 Planilhas Inteligentes',
    description: 'Histórico de clientes, controle de satisfação e análise mensal dos clientes, tudo integrado, simples de preencher e integrado aos dashboards.'
  },
  {
    icon: BarChart3,
    title: '6 Dashboards Estratégicos',
    description: 'Visão completa das métricas gerais, metas, cancelamentos, satisfação, comparativos e tendências. Métricas e gráficos que viram decisões assertivas em segundos.'
  }
];

const faqs = [
  {
    question: 'O dashboard se atualiza sozinho?',
    answer: 'Sim! Basta preencher e atualizar as planilhas que todos os gráficos e métricas do dash se atualizarão automaticamente. Inclusive você pode compartilhá-lo com várias pessoas.'
  },
  {
    question: 'Posso mesmo adquirir apenas partes da solução?',
    answer: 'Sim! Você que decide o que deseja inicialmente, com o restante podendo ser adicionado por partes, conforme sua necessidade e condições.'
  },
  {
    question: 'Quanto tempo leva para implementar?',
    answer: 'Depende do escopo escolhido e de sua disponibilidade para validar tudo. Soluções parciais podem ser entregues em poucos dias, enquanto a solução completa leva algumas semanas.'
  },
  {
    question: 'Tudo pode ser personalizado com as cores da minha agência?',
    answer: 'Com certeza! Não apenas as cores como também os gráficos, métricas, e tudo mais que você desejar; só ir sugerindo durante a implementação.'
  },
  {
    question: 'O que acontece após a entrega?',
    answer: 'Você contará com meu suporte contínuo para dúvidas, ajustes e melhorias pontuais, mesmo após a conclusão. Inclusive, pode pagar só após estar tudo 100% também!'
  }
];


const agencyLogos = [
  'Okbox', 'Gooxie Group', 'WM Digital', 'Keep Ads',
  'Aw5 digital', 'Blue Marketing', 'Agência Propulse', 'Agência Meio Digital',
  'Agência Acelera+', 'Academia das Redes Sociais', 'THS Mídias Digitais', 'M2up', 'Audens Company',
  'Agência Oásis', 'Agência Turbo 7', 'Agência BUS', 'Agência Backstage', 'Agência Take One', 'CML Gestão'
];

const placeholderImages = [
  '/assets/resumogeral.png',
  '/assets/HistClientes.jpeg',
  '/assets/satisfacao.png',
  '/assets/HistSatisfacao.jpeg',
  '/assets/controlemensal.png',
  '/assets/ContMensal.jpeg',
  '/assets/cancelamentos.png',
  '/assets/ativosxcancelados.jpeg',
  '/assets/compgestores.png'
];

const RetencaoAgenciasLanding = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [currentImage, setCurrentImage] = useState(0);
  const carouselRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const nextImage = () => setCurrentImage((p) => (p + 1) % placeholderImages.length);
  const prevImage = () => setCurrentImage((p) => (p - 1 + placeholderImages.length) % placeholderImages.length);

  return (
    <>
          <Helmet>
            <title>Gestão de Retenção para Agências (LTV & Churn)</title>
            <meta name="description" content="Dashboards focados em métricas de retenção. Reduza o churn da sua agência acompanhando o sucesso do cliente de perto." />
          </Helmet>
    <div className="page-retencao-landing">

      {/* ===== HERO ===== */}
<section className="rl-hero" data-testid="rl-hero" style={{ overflow: 'hidden', padding: '40px 0'  }}> {/* overflow hidden para garantir que nada saia da tela no mobile */}
  <div className="rl-container"> 
    <Link to="/services" className="back-link" style={{ marginBottom: '20px' }} data-testid="bv-back-link">
      <ArrowLeft size={18} />
      Voltar aos Serviços
    </Link>

    {/* AJUSTE 1: O GRID PAI */}
    <div className="bv-hero-grid" style={{ 
      display: 'grid', 
      gridTemplateColumns: '1.4fr 0.6fr', // Mantém a proporção Texto/Cartões
      gap: '50px', 
      alignItems: 'center',              // <--- ISTO É O QUE ALINHA TUDO AO CENTRO VERTICALMENTE
      marginTop: '20px'                   // Espaço após o botão voltar
    }}>
      
      {/* Coluna da Esquerda: Texto */}
      <div className="rl-hero-content">
        <div className="rl-hero-badge">Validado em +20 Agências</div>
        <h1 className="rl-hero-title" style={{ marginTop: '20px', fontSize: '2.4rem', lineHeight: '1.2' }}>
          Reduza o Churn e
          <span className="rl-highlight"> Aumente a Retenção</span> dos clientes da Sua Agência de Marketing
        </h1>
        <p className="rl-hero-subtitle" style={{ marginTop: '00px', marginBottom: '30px', fontSize: '1.1rem', opacity: '0.9' }}>
          A solução de BI que transforma dados de planilhas em dashs do Looker Studio para suas tomadas de decisões estratégicas.
          Dados, Gráficos e Métricas que mostram exatamente onde você deve agir para reter melhor seus clientes.
        </p>
        <div className="rl-hero-cta-group">
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn-primary btn-large" data-testid="rl-hero-cta">
            Quero Aumentar Minha Retenção
            <ArrowRight size={20} />
          </a>
        </div>
      </div>

      {/* AJUSTE 2: A COLUNA DOS CARTÕES (Com o novo cartão) */}
      <div className="rl-hero-visual hide-on-mobile" aria-hidden="true" style={{ marginTop: '00px',
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',   // Mantém alinhado à direita
        justifyContent: 'center',
        gap: '15px',              // <--- DIMINUÍ O ESPAÇO para caber o 5º cartão
        paddingRight: '60px',
        width: '100%',
        height: 'auto'            // Deixa a altura se adaptar ao conteúdo
      }}>
        
        {/* NOVO CARTÃO: CLIENTES ATIVOS (Topo) */}
        <div style={{
          backgroundColor: '#1a1a1a',
          padding: '10px 18px', // <--- REDUZI O PADDING VERTICAL de 12px para 10px
          borderRadius: '12px',
          border: '1px solid rgba(0, 204, 102, 0.2)',
          width: '200px', 
          boxShadow: '0 8px 16px rgba(0,0,0,0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
            <Users size={16} color="#00cc66" />
            <span style={{ fontSize: '15px', opacity: 0.7, textTransform: 'uppercase' }}>Clientes Ativos</span>
          </div>
          <h3 style={{ fontSize: '20px', margin: 0, color: '#00cc66' }}>+30%</h3>
        </div>

        {/* Cartão 2: MRR */}
        <div style={{
          backgroundColor: '#1a1a1a',
          padding: '10px 18px',
          borderRadius: '12px',
          border: '1px solid rgba(0, 204, 102, 0.2)',
          width: '200px',
          boxShadow: '0 8px 16px rgba(0,0,0,0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
            <DollarSign size={16} color="#00cc66" />
            <span style={{ fontSize: '15px', opacity: 0.7, textTransform: 'uppercase' }}>MRR Growth</span>
          </div>
          <h3 style={{ fontSize: '20px', margin: 0, color: '#00cc66' }}>+22.5%</h3>
        </div>

        {/* Cartão 3: LTV */}
        <div style={{
          backgroundColor: '#1a1a1a',
          padding: '10px 18px',
          borderRadius: '12px',
          border: '1px solid rgba(0, 204, 102, 0.2)',
          width: '200px', 
          boxShadow: '0 8px 16px rgba(0,0,0,0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
            <TrendingUp size={16} color="#00cc66" />
            <span style={{ fontSize: '15px', opacity: 0.7, textTransform: 'uppercase' }}>LTV Médio</span>
          </div>
          <h3 style={{ fontSize: '20px', margin: 0, color: '#00cc66' }}>+15%</h3>
        </div>

        {/* Cartão 4: Satisfação */}
        <div style={{
          backgroundColor: '#1a1a1a',
          padding: '10px 18px',
          borderRadius: '12px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          width: '200px',
          boxShadow: '0 8px 16px rgba(0,0,0,0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
            <Smile size={16} color="#FFD700" />
            <span style={{ fontSize: '15px', opacity: 0.7, textTransform: 'uppercase' }}>Satisfação</span>
          </div>
          <h3 style={{ fontSize: '20px', margin: 0 }}>85%</h3>
        </div>

        {/* Cartão 5: Churn */}
        <div style={{
          backgroundColor: '#1a1a1a',
          padding: '10px 18px',
          borderRadius: '12px',
          border: '1px solid rgba(255, 77, 77, 0.2)',
          width: '200px',
          boxShadow: '0 8px 16px rgba(0,0,0,0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
            <TrendingDown size={16} color="#ff4d4d" />
            <span style={{ fontSize: '15px', opacity: 0.7, textTransform: 'uppercase' }}>Churn Rate</span>
          </div>
          <h3 style={{ fontSize: '20px', margin: 0, color: '#ff4d4d' }}>3%</h3>
        </div>

      </div>

    </div>
  </div>
</section>

      {/* ===== DORES ===== */}
      <section className="rl-pain-points" data-testid="rl-pain-points">
        <div className="rl-container">
          <div className="rl-section-header">
            <h2 className="rl-section-title">Por que as agências perdem clientes?</h2>
            <p className="rl-section-subtitle">
              Os 3 problemas que corroem a retenção da maioria das agências de marketing
            </p>
          </div>
          <div className="rl-pain-grid">
            {painPoints.map((pain, i) => {
              const Icon = pain.icon;
              return (
                <div key={i} className="rl-pain-card" data-testid={`rl-pain-card-${i}`}>
                  <div className="rl-pain-icon">
                    <Icon size={28} />
                  </div>
                  <h3 className="rl-pain-title">{pain.title}</h3>
                  <p className="rl-pain-description">{pain.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== SOLUÇÃO ===== */}
      <section className="rl-solution" data-testid="rl-solution">
        <div className="rl-container">
          <div className="rl-section-header">
            <h2 className="rl-section-title">O Ecossistema Completo de Retenção</h2>
            <p className="rl-section-subtitle">
              3 planilhas + 6 dashboards que dão visibilidade total sobre seus clientes recorrentes
            </p>
          </div>

          <div className="rl-solution-cards">
            {solutionItems.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="rl-solution-card" data-testid={`rl-solution-card-${i}`}>
                  <div className="rl-solution-icon">
                    <Icon size={32} />
                  </div>
                  <h3 className="rl-solution-card-title">{item.title}</h3>
                  <p className="rl-solution-card-text">{item.description}</p>
                </div>
              );
            })}
          </div>

          <div className="rl-metrics-strip">
            {solutionMetrics.map((metric, i) => {
              const Icon = metric.icon;
              return (
                <div key={i} className="rl-metric-item" data-testid={`rl-metric-${i}`}>
                  <Icon size={20} className="rl-metric-icon" />
                  <div>
                    <span className="rl-metric-label">{metric.label}</span>
                    <span className="rl-metric-desc">{metric.description}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== DIFERENCIAL ===== */}
      <section className="rl-differentiator" data-testid="rl-differentiator">
        <div className="rl-container">
          <div className="rl-diff-grid">
            <div className="rl-diff-card" data-testid="rl-diff-complete">
              <div className="rl-diff-icon">
                <Package size={32} />
              </div>
              <h3 className="rl-diff-title">Solução Completa</h3>
              <p className="rl-diff-text">
                Adquira o ecossistema inteiro: 3 planilhas + 6 dashboards integrados.
                Ideal para quem quer visibilidade total desde o primeiro dia.
              </p>
              <a href={DASHBOARD} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="rl-diff-cta-complete">
                Ver Modelo de Dashboard
                <ArrowRight size={18} />
              </a>
            </div>
            <div className="rl-diff-card" data-testid="rl-diff-partial">
              <div className="rl-diff-icon">
                <ShieldCheck size={32} />
              </div>
              <h3 className="rl-diff-title">Solução Particionada</h3>
              <p className="rl-diff-text">
                Comece com o que precisa agora e vá adicionando depois.
                Planilhas e dashboards podem ser adquiridos separadamente.
              </p>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn-secondary" data-testid="rl-diff-cta-partial">
                Montar Meu Pacote
                <ArrowRight size={18} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PROVA SOCIAL / LOGOS ===== */}
      <section className="rl-social-proof" data-testid="rl-social-proof">
        <div className="rl-container">
          <div className="rl-section-header">
            <h2 className="rl-section-title">Algumas das Agências Que Já Confiam na Solução</h2>
          </div>
        </div>
        <div className="rl-logos-track-wrapper">
          <div className="rl-logos-track" ref={carouselRef}>
            {[...agencyLogos, ...agencyLogos].map((name, i) => (
              <div key={i} className="rl-logo-item" data-testid={`rl-logo-${i}`}>
                <span>{name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CARROSSEL DE IMAGENS ===== */}
      <section className="rl-showcase" data-testid="rl-showcase">
        <div className="rl-container">
          <div className="rl-section-header">
            <h2 className="rl-section-title">Veja as Planilhas e Dashboards</h2>
            <p className="rl-section-subtitle">
              Exemplos reais do que você receberá ao adquirir a solução
            </p>
          </div>
          <div className="portfolio-detail-container">
            <div className="portfolio-image-carousel">
              <img
                src={placeholderImages[currentImage]}
                alt={`Dashboard exemplo ${currentImage + 1}`}
                className="portfolio-carousel-image"
              />
              {placeholderImages.length > 1 && (
                <>
                  <button className="carousel-btn carousel-btn-prev" onClick={prevImage} aria-label="Imagem anterior" data-testid="rl-carousel-prev">
                    <ChevronLeft size={24} />
                  </button>
                  <button className="carousel-btn carousel-btn-next" onClick={nextImage} aria-label="Próxima imagem" data-testid="rl-carousel-next">
                    <ChevronRight size={24} />
                  </button>
                  <div className="carousel-indicators">
                    {placeholderImages.map((_, index) => (
                      <button
                        key={index}
                        className={`carousel-indicator ${index === currentImage ? 'active' : ''}`}
                        onClick={() => setCurrentImage(index)}
                        aria-label={`Ir para imagem ${index + 1}`}
                      />
                    ))}
                  </div>
                  <div className="carousel-counter">
                    {currentImage + 1} / {placeholderImages.length}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="rl-faq" data-testid="rl-faq">
        <div className="rl-container rl-faq-container">
          <div className="rl-section-header">
            <div className="faq-icon-wrapper">
              <HelpCircle size={32} />
            </div>
            <h2 className="rl-section-title">Perguntas Frequentes</h2>
          </div>
          <div className="faq-grid-modern">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`faq-card-modern ${openFaq === index ? 'faq-card-open' : ''}`}
              >
                <button
                  className="faq-question-modern"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  data-testid={`rl-faq-question-${index}`}
                >
                  <span className="faq-number">{String(index + 1).padStart(2, '0')}</span>
                  <span className="faq-question-text">{faq.question}</span>
                  <ChevronDown
                    size={20}
                    className={`faq-chevron ${openFaq === index ? 'faq-chevron-open' : ''}`}
                  />
                </button>
                <div className={`faq-answer-modern ${openFaq === index ? 'faq-answer-visible' : ''}`}>
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA FINAL ===== */}
      <section className="rl-final-cta" data-testid="rl-final-cta">
        <div className="rl-container">
          <div className="rl-final-cta-content">
            <h2 className="rl-final-cta-title">Pronto Para Melhorar a Retenção da Sua Agência, Ter Mais Clareza das Métricas e Reduzir o Churn?</h2>
            <p className="rl-final-cta-text">
              Vamos conversar e descubrir como essa incrível solução pode se adaptar à realidade da sua operação e levá-la para outro nível 🚀
            </p>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn-primary btn-large" data-testid="rl-final-cta-btn">
              Quero Aumentar Minha Retenção
              <ArrowRight size={20} />
            </a>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default RetencaoAgenciasLanding;
